import { supabase } from '../lib/supabase';

// Reserved row name used to persist account-level preferences inside user_themes.
// This row is filtered out of all display lists.
const PREFS_ROW_NAME = '__account_prefs__';

const getUser = async () => {
  // Respect super-admin impersonation so themes are scoped to the target account
  const impersonationData = sessionStorage?.getItem('superAdminImpersonating');
  if (impersonationData) {
    try {
      const parsed = JSON.parse(impersonationData);
      if (parsed?.targetId) return parsed.targetId;
    } catch (_) {}
  }
  const { data: { user }, error } = await supabase?.auth?.getUser();
  if (error || !user) throw new Error('Not authenticated');
  return user.id;
};

export const userThemesService = {
  async getThemes() {
    try {
      const userId = await getUser();
      const { data, error } = await supabase
        ?.from('user_themes')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.neq('name', PREFS_ROW_NAME)
        ?.order('created_at', { ascending: false });
      if (error) {
        console.error('Error fetching user themes:', error?.message);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error('Error in getThemes:', err);
      return [];
    }
  },

  async saveTheme(name, config) {
    try {
      const userId = await getUser();
      const { data, error } = await supabase
        ?.from('user_themes')
        ?.insert({ user_id: userId, name: name?.trim(), config })
        ?.select()
        ?.single();
      if (error) throw new Error(error?.message);
      return data;
    } catch (err) {
      console.error('Error in saveTheme:', err);
      throw err;
    }
  },

  async updateThemeName(id, name) {
    try {
      const userId = await getUser();
      const { data, error } = await supabase
        ?.from('user_themes')
        ?.update({ name: name?.trim(), updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.eq('user_id', userId)
        ?.select()
        ?.single();
      if (error) throw new Error(error?.message);
      return data;
    } catch (err) {
      console.error('Error in updateThemeName:', err);
      throw err;
    }
  },

  async deleteTheme(id) {
    try {
      const userId = await getUser();
      const { error } = await supabase
        ?.from('user_themes')
        ?.delete()
        ?.eq('id', id)
        ?.eq('user_id', userId);
      if (error) throw new Error(error?.message);
    } catch (err) {
      console.error('Error in deleteTheme:', err);
      throw err;
    }
  },

  /**
   * Mark one theme as the account-level default for new forms.
   *
   * Persisted as a reserved marker row (name = PREFS_ROW_NAME) in user_themes
   * so it inherits the existing RLS policy and respects super-admin impersonation
   * via getUser() — no DB schema change required.
   */
  async setDefaultTheme(id) {
    try {
      const userId = await getUser();
      // Upsert the prefs row: update if it exists, insert if it doesn't.
      const { data: existing } = await supabase
        ?.from('user_themes')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('name', PREFS_ROW_NAME)
        ?.maybeSingle();

      if (existing?.id) {
        const { error } = await supabase
          ?.from('user_themes')
          ?.update({ config: { defaultThemeId: id }, updated_at: new Date()?.toISOString() })
          ?.eq('id', existing.id)
          ?.eq('user_id', userId);
        if (error) throw new Error(error?.message);
      } else {
        const { error } = await supabase
          ?.from('user_themes')
          ?.insert({ user_id: userId, name: PREFS_ROW_NAME, config: { defaultThemeId: id } });
        if (error) throw new Error(error?.message);
      }
    } catch (err) {
      console.error('Error in setDefaultTheme:', err);
      throw err;
    }
  },

  /**
   * Clear the account-level default (no theme is default for new forms afterwards).
   */
  async clearDefaultTheme() {
    try {
      const userId = await getUser();
      const { error } = await supabase
        ?.from('user_themes')
        ?.update({ config: { defaultThemeId: null }, updated_at: new Date()?.toISOString() })
        ?.eq('user_id', userId)
        ?.eq('name', PREFS_ROW_NAME);
      if (error) throw new Error(error?.message);
    } catch (err) {
      console.error('Error in clearDefaultTheme:', err);
      throw err;
    }
  },

  /**
   * Return the user_themes row that is the account-level default, or null.
   *
   * Reads the defaultThemeId from the reserved prefs marker row, then fetches
   * the actual theme row so callers get the full config object.
   */
  async getDefaultTheme() {
    try {
      const userId = await getUser();
      const { data: prefsRow, error: prefsError } = await supabase
        ?.from('user_themes')
        ?.select('config')
        ?.eq('user_id', userId)
        ?.eq('name', PREFS_ROW_NAME)
        ?.maybeSingle();

      if (prefsError) {
        console.error('Error fetching prefs row:', prefsError?.message);
        return null;
      }

      const defaultThemeId = prefsRow?.config?.defaultThemeId;
      if (!defaultThemeId) return null;

      const { data, error } = await supabase
        ?.from('user_themes')
        ?.select('*')
        ?.eq('id', defaultThemeId)
        ?.eq('user_id', userId)
        ?.maybeSingle();
      if (error) {
        console.error('Error fetching default theme row:', error?.message);
        return null;
      }
      return data || null;
    } catch (err) {
      console.error('Error in getDefaultTheme:', err);
      return null;
    }
  },
};
